import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { GeolocationState } from '../hooks/useGeolocation';
import type { NearbyState } from '../hooks/useNearby';
import type { SavedProject } from '../hooks/useSavedItems';
import { Code, Projects } from 'trace-viewer/cms';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { apiConfig, BLOB_BASE_URL } from '../auth';
import { MapView } from './MapView';
import { CodeModal } from './CodeModal';
import { useDragScroll } from '../hooks/useDragScroll';

interface Props {
    onSelectScene: (scene: SceneResponseDto) => void;
    initialCodeError?: string | null;
    tab: TabMode;
    onTabChange: (tab: TabMode) => void;
    geo: GeolocationState;
    nearby: NearbyState;
    expandedProjectId: string | null;
    expandedScenes: SceneResponseDto[];
    onExpandedChange: (id: string | null, scenes: SceneResponseDto[]) => void;
    savedProjects: SavedProject[];
    savedScenes: { scene: SceneResponseDto; savedAt: string }[];
    onSaveProject: (project: ProjectResponseDto, scenes: SceneResponseDto[]) => void;
    onRemoveProject: (projectId: string) => void;
    onRemoveScene: (sceneId: string) => void;
    /** When non-null, curated ?projects= mode: show only these projects instead of nearby */
    curatedProjects: ProjectResponseDto[] | null;
    curatedLoading: boolean;
    /** True while a ?code= query is being resolved */
    codeQueryLoading: boolean;
    /** Project ID resolved from ?code= — auto-select in Saved tab */
    codeQueryProjectId: string | null;
}

export type TabMode = 'experiences' | 'saved' | 'map';

/** Sentinel ID used to treat independent scenes as a virtual project card. */
const INDEPENDENT_SCENES_ID = '__independent__';

export function DiscoveryView({ onSelectScene, initialCodeError, tab, onTabChange, geo, nearby, expandedProjectId, expandedScenes, onExpandedChange, savedProjects, savedScenes, onSaveProject, onRemoveProject, onRemoveScene, curatedProjects, curatedLoading, codeQueryLoading, codeQueryProjectId }: Props) {
    const { token, loading: authLoading } = useAuth();

    const setTab = useCallback((newTab: TabMode) => {
        const url = newTab === 'map' ? '/map' : newTab === 'saved' ? '/saved' : '/';
        window.history.pushState(null, '', url);
        onTabChange(newTab);
    }, [onTabChange]);

    // Sync tab with browser back/forward within discovery view
    useEffect(() => {
        const onPopState = () => {
            const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
            const newTab: TabMode = path === 'map' ? 'map' : path === 'saved' ? 'saved' : 'experiences';
            onTabChange(newTab);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [onTabChange]);

    const [mapSelectError, setMapSelectError] = useState<string | null>(null);
    const [codeLooking, setCodeLooking] = useState(false);
    const [codeError, setCodeError] = useState<string | null>(null);
    const [showCodeModal, setShowCodeModal] = useState(false);

    const [loadingProject, setLoadingProject] = useState(false);

    // Ref to track the auto-dismiss timer so it can be cleared on unmount (prevents setTimeout leak)
    const mapSelectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks a project ID that should be scrolled into view after tab switch
    const scrollToProjectRef = useRef<string | null>(null);

    useEffect(() => {
        return () => {
            if (mapSelectTimerRef.current !== null) {
                clearTimeout(mapSelectTimerRef.current);
            }
        };
    }, []);

    const selectProject = async (project: ProjectResponseDto) => {
        const id = project.id!;

        if (expandedProjectId === id) return;

        if (project.scenes?.length) {
            onExpandedChange(id, project.scenes);
            return;
        }

        if (!token) return;
        onExpandedChange(id, []);
        setLoadingProject(true);
        try {
            const res = await new Projects(apiConfig(token)).scenesList(id);
            onExpandedChange(id, res.data ?? []);
        } catch {
            // Silently fail
        } finally {
            setLoadingProject(false);
        }
    };

    const selectIndependentScenes = () => {
        if (expandedProjectId === INDEPENDENT_SCENES_ID) return;
        onExpandedChange(INDEPENDENT_SCENES_ID, displayIndependentScenes);
    };

    // State for the expanded project within the Saved tab
    const [savedExpandedProjectId, setSavedExpandedProjectId] = useState<string | null>(null);
    const [savedExpandedScenes, setSavedExpandedScenes] = useState<SceneResponseDto[]>([]);
    const [savedLoadingProject, setSavedLoadingProject] = useState(false);

    const savedProjectsScrollRef = useDragScroll();
    const savedScenesScrollRef = useDragScroll();

    // When ?code= resolves a project, select it in the Saved tab
    useEffect(() => {
        if (!codeQueryProjectId) return;
        const match = savedProjects.find(s => s.project.id === codeQueryProjectId);
        if (match) {
            setSavedExpandedProjectId(match.project.id!);
            setSavedExpandedScenes(match.scenes);
        }
    }, [codeQueryProjectId, savedProjects]);

    // Auto-expand the first saved project when switching to saved tab (if nothing selected)
    useEffect(() => {
        if (tab !== 'saved' || savedExpandedProjectId || codeQueryLoading || savedProjects.length === 0) return;
        const first = savedProjects[0];
        setSavedExpandedProjectId(first.project.id!);
        setSavedExpandedScenes(first.scenes);
    }, [tab, savedProjects, savedExpandedProjectId, codeQueryLoading]);

    const selectSavedProject = async (saved: SavedProject) => {
        const id = saved.project.id!;
        if (savedExpandedProjectId === id) return;

        // Use cached scenes if available
        if (saved.scenes.length > 0) {
            setSavedExpandedProjectId(id);
            setSavedExpandedScenes(saved.scenes);
            return;
        }

        if (!token) return;
        setSavedExpandedProjectId(id);
        setSavedExpandedScenes([]);
        setSavedLoadingProject(true);
        try {
            const res = await new Projects(apiConfig(token)).scenesList(id);
            const scenes = res.data ?? [];
            setSavedExpandedScenes(scenes);
            // Update the saved entry with fetched scenes
            onSaveProject(saved.project, scenes);
        } catch {
            // Silently fail
        } finally {
            setSavedLoadingProject(false);
        }
    };

    const handleCodeSubmit = async (code: string) => {
        const trimmed = code.trim();
        if (!trimmed || !token) return;

        setCodeLooking(true);
        setCodeError(null);

        try {
            const res = await new Code(apiConfig(token)).codeDetail(trimmed);
            const result = res.data;

            if (result.isScene && result.scene) {
                setShowCodeModal(false);
                onSelectScene(result.scene);
            } else if (result.isProject && result.project) {
                // Save the project to the Saved tab and switch to it
                let scenes = result.project.scenes ?? [];
                if (scenes.length === 0) {
                    const scenesRes = await new Projects(apiConfig(token)).scenesList(result.project.id!);
                    scenes = scenesRes.data ?? [];
                }
                onSaveProject(result.project, scenes);
                setSavedExpandedProjectId(result.project.id!);
                setSavedExpandedScenes(scenes);
                setShowCodeModal(false);
                setTab('saved');
            } else {
                setCodeError('Code not found');
            }
        } catch {
            setCodeError('Could not find that code');
        } finally {
            setCodeLooking(false);
        }
    };

    const handleMapSelectProject = useCallback((project: ProjectResponseDto, scenes: SceneResponseDto[]) => {
        setMapSelectError(null);
        // Switch to Experience tab and expand the selected project
        setTab('experiences');
        onExpandedChange(project.id ?? null, scenes);
        scrollToProjectRef.current = project.id ?? null;
        if (scenes.length === 0) {
            setMapSelectError('No experiences available for this project');
            if (mapSelectTimerRef.current !== null) {
                clearTimeout(mapSelectTimerRef.current);
            }
            mapSelectTimerRef.current = setTimeout(() => {
                setMapSelectError(null);
                mapSelectTimerRef.current = null;
            }, 3000);
        }
    }, []);

    const resolveUrl = (url: string | null | undefined) => {
        if (!url) return undefined;
        if (url.startsWith('http')) return url;
        return `${BLOB_BASE_URL}/${url.replace(/^\//, '')}`;
    };

    // Curated mode: ?projects= query overrides nearby data
    const isCurated = curatedProjects !== null;
    const displayProjects = isCurated ? curatedProjects : nearby.projects;
    const displayIndependentScenes = isCurated ? [] : nearby.independentScenes;

    const nearbyProjectIds = useMemo(
        () => new Set(displayProjects.map(p => p.id).filter(Boolean) as string[]),
        [displayProjects],
    );

    // nearby.loading starts as false and only flips to true after geo resolves,
    // so bridge the gap: if we have coords but nearby hasn't produced results or an error yet, treat it as loading.
    const nearbyPending = !isCurated && (nearby.loading || (geo.latitude != null && !nearby.error && nearby.projects.length === 0 && nearby.independentScenes.length === 0));
    const isLoading = authLoading || (isCurated ? curatedLoading : (geo.loading || nearbyPending));
    const loadingMessage = authLoading ? 'Connecting...' : curatedLoading ? 'Loading projects...' : geo.loading ? 'Getting location...' : nearbyPending ? 'Finding nearby experiences...' : null;

    const hasIndependentScenes = displayIndependentScenes.length > 0;
    const showContent = !isLoading;

    const projectsScrollRef = useDragScroll();
    const scenesScrollRef = useDragScroll();

    // Auto-expand the first project (or independent scenes) once data arrives,
    // but only if nothing is already expanded (preserves state across remounts)
    useEffect(() => {
        if (expandedProjectId) return;
        if (displayProjects.length > 0) {
            const first = displayProjects[0];
            if (first.scenes?.length) {
                onExpandedChange(first.id!, first.scenes);
            } else if (token && first.id) {
                onExpandedChange(first.id, []);
                setLoadingProject(true);
                void new Projects(apiConfig(token)).scenesList(first.id).then(res => {
                    onExpandedChange(first.id!, res.data ?? []);
                }).catch((err: unknown) => { console.warn('Failed to load scenes for project', first.id, err); }).finally(() => {
                    setLoadingProject(false);
                });
            }
        } else if (displayIndependentScenes.length > 0) {
            onExpandedChange(INDEPENDENT_SCENES_ID, displayIndependentScenes);
        }
    }, [displayProjects, displayIndependentScenes, token, expandedProjectId, onExpandedChange]);

    return (
        <div className="discovery">
            {/* Map tab — hidden (not unmounted) when list is active */}
            <div className="map-tab-container" style={tab !== 'map' ? { display: 'none' } : undefined}>
                {mapSelectError && (
                    <div className="map-select-error">{mapSelectError}</div>
                )}
                <MapView onSelectProject={handleMapSelectProject} onSelectScene={onSelectScene} nearbyProjectIds={nearbyProjectIds} curatedProjects={curatedProjects} />
            </div>

            {/* List view — hidden (not unmounted) when other tabs are active */}
            <div className="discovery-list" style={tab !== 'experiences' ? { display: 'none' } : undefined}>

            {/* Header */}
            <div className="discovery-header">
                <div className="discovery-header-row">
                    <h1 className="discovery-title">Experiences</h1>
                    <button
                        className="discovery-code-link"
                        onClick={() => {
                            setCodeError(null);
                            setShowCodeModal(true);
                        }}
                    >
                        Use a Code
                    </button>
                </div>
                <p className="discovery-subtitle">
                    {isCurated ? 'Curated experiences' : 'Discover nearby AR experiences'}
                </p>
            </div>

            {/* Content */}
            <div className="nearby-section">
                {isLoading && (
                    <div className="trace-loading-status">
                        <div className="trace-loading-logo-container">
                            <div className="trace-loading-orbit" />
                            <svg className="trace-loading-logo" width="200" height="200" viewBox="0 0 166 166" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g filter="url(#tl_shadow)">
                                    <path d="M81 159C124.078 159 159 124.078 159 81C159 37.9218 124.078 3 81 3C37.9218 3 3 37.9218 3 81C3 124.078 37.9218 159 81 159Z" fill="url(#tl_gradient)" />
                                </g>
                                <g filter="url(#tl_glow1)">
                                    <path d="M56 52H106V64H88V117H74V64H56V52Z" stroke="#00F0FF" strokeOpacity="0.5" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <path d="M56 52H106V64H88V117H74V64H56V52Z" stroke="#00F0FF" strokeLinecap="round" strokeLinejoin="round" />
                                <g filter="url(#tl_glow2)">
                                    <path d="M56 52H106V64H88V117H74L75 64H57L56 52Z" stroke="#00F0FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                    <filter id="tl_shadow" x="0" y="0" width="166" height="166" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                        <feOffset dx="2" dy="2" />
                                        <feGaussianBlur stdDeviation="2.5" />
                                        <feComposite in2="hardAlpha" operator="out" />
                                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0" />
                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                                    </filter>
                                    <filter id="tl_glow1" x="52.5" y="48.5" width="57" height="72" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                        <feGaussianBlur stdDeviation="1.5" result="effect1_foregroundBlur" />
                                    </filter>
                                    <filter id="tl_glow2" x="31" y="27" width="100" height="115" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                        <feGaussianBlur stdDeviation="12" result="effect1_foregroundBlur" />
                                    </filter>
                                    <radialGradient id="tl_gradient" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(5931 5307) scale(13260)">
                                        <stop offset="0.0961538" stopColor="#2CA1C9" />
                                        <stop offset="0.524038" stopColor="#1F5F86" />
                                        <stop offset="1" stopColor="#0B2B45" />
                                    </radialGradient>
                                </defs>
                            </svg>
                        </div>
                        <div className="trace-loading-label">{loadingMessage}</div>
                    </div>
                )}
                {geo.error && !isCurated && (
                    <div className="status-message geo-error">
                        <span>Location unavailable. Enter a code to find content.</span>
                        {geo.permissionState === 'denied'
                            ? <p className="geo-hint">Location is blocked in your browser. To re-enable, tap the lock icon in the address bar and allow location access, then reload.</p>
                            : <button className="geo-retry-button" onClick={geo.retry} disabled={geo.loading}>
                                {geo.loading ? 'Requesting…' : 'Grant location access'}
                            </button>
                        }
                    </div>
                )}
                {nearby.error && <div className="status-message">{nearby.error}</div>}

                {/* Projects — horizontal scroll of tall cards */}
                {showContent && (displayProjects.length > 0 || hasIndependentScenes) && (
                    <>
                        <div className="projects-scroll" ref={projectsScrollRef}>
                            {displayProjects.map(project => (
                                <div
                                    key={project.id}
                                    data-project-id={project.id}
                                    ref={el => {
                                        if (el && scrollToProjectRef.current === project.id) {
                                            scrollToProjectRef.current = null;
                                            requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }));
                                        }
                                    }}
                                    className={`hero-card ${expandedProjectId === project.id ? 'selected' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={project.name ?? 'Untitled Project'}
                                    onClick={() => selectProject(project)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectProject(project); } }}
                                >
                                    {(project.graphicUrl || project.logoUrl) ? (
                                        <img
                                            className="hero-card-image"
                                            src={resolveUrl(project.graphicUrl || project.logoUrl)}
                                            alt=""
                                        />
                                    ) : (
                                        <div className="hero-card-placeholder">
                                            {(project.name ?? 'P')[0]}
                                        </div>
                                    )}
                                    <div className="hero-card-gradient" />
                                    <div className="hero-card-text">
                                        <div className="hero-card-name">
                                            {project.name ?? 'Untitled Project'}
                                        </div>
                                        {project.description && (
                                            <div className="hero-card-desc">{project.description}</div>
                                        )}
                                    </div>
                                    {loadingProject && expandedProjectId === project.id && (
                                        <div className="hero-card-loading">
                                            <div className="loading-spinner" />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Independent scenes as a virtual project card */}
                            {hasIndependentScenes && (
                                <div
                                    className={`hero-card ${expandedProjectId === INDEPENDENT_SCENES_ID ? 'selected' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Independent Scenes"
                                    onClick={selectIndependentScenes}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectIndependentScenes(); } }}
                                >
                                    <div className="hero-card-placeholder">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                                            <rect x="3" y="3" width="7" height="7" rx="1.5" />
                                            <rect x="14" y="3" width="7" height="7" rx="1.5" />
                                            <rect x="3" y="14" width="7" height="7" rx="1.5" />
                                            <rect x="14" y="14" width="7" height="7" rx="1.5" />
                                        </svg>
                                    </div>
                                    <div className="hero-card-gradient" />
                                    <div className="hero-card-text">
                                        <div className="hero-card-name">Independent Scenes</div>
                                        <div className="hero-card-desc">
                                            {displayIndependentScenes.length} independent scene{displayIndependentScenes.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Expanded project's scenes */}
                        {expandedProjectId && !loadingProject && (
                            <div className="expanded-project-scenes">
                                <h3 className="expanded-scenes-label">
                                    {expandedProjectId === INDEPENDENT_SCENES_ID
                                        ? 'Independent Scenes'
                                        : `${displayProjects.find(p => p.id === expandedProjectId)?.name ?? 'Project'} Scenes`}
                                </h3>
                                {expandedScenes.length === 0 ? (
                                    <div className="expanded-scenes-empty">No scenes in this project</div>
                                ) : (
                                    <div className="scenes-scroll" ref={scenesScrollRef}>
                                        {expandedScenes.map(scene => (
                                            <div
                                                key={scene.id}
                                                className="scene-card-square"
                                                onClick={() => onSelectScene(scene)}
                                            >
                                                {scene.imageUrl ? (
                                                    <img
                                                        className="scene-card-image"
                                                        src={resolveUrl(scene.imageUrl)}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="scene-card-placeholder">
                                                        {(scene.name ?? 'S')[0]}
                                                    </div>
                                                )}
                                                <div className="scene-card-name">
                                                    {scene.name ?? 'Untitled Scene'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Empty state */}
                {showContent && !geo.error &&
                    displayProjects.length === 0 && displayIndependentScenes.length === 0 && (
                    <div className="status-message">
                        No nearby content found. Try using a code.
                    </div>
                )}
            </div>

            </div>{/* end list-view wrapper */}

            {/* Saved tab — hidden (not unmounted) when other tabs are active */}
            <div className="discovery-list saved-list" style={tab !== 'saved' ? { display: 'none' } : undefined}>
                <div className="discovery-header">
                    <div className="discovery-header-row">
                        <h1 className="discovery-title">Saved</h1>
                        <button
                            className="discovery-code-link"
                            onClick={() => {
                                setCodeError(null);
                                setShowCodeModal(true);
                            }}
                        >
                            Use a Code
                        </button>
                    </div>
                    <p className="discovery-subtitle">
                        Recently viewed projects &amp; scenes
                    </p>
                </div>

                <div className="nearby-section">
                    {codeQueryLoading && (
                        <div className="saved-loading-skeleton">
                            <div className="hero-card-skeleton">
                                <div className="skeleton-shimmer" />
                            </div>
                            <div className="saved-loading-label">Looking up code...</div>
                        </div>
                    )}
                    {!codeQueryLoading && savedProjects.length === 0 && savedScenes.length === 0 ? (
                        <div className="status-message">
                            No saved items yet. Use a code to add a project.
                        </div>
                    ) : (
                        <>
                            {/* Saved projects — horizontal scroll of hero cards */}
                            {savedProjects.length > 0 && (
                                <>
                                    <div className="projects-scroll" ref={savedProjectsScrollRef}>
                                        {savedProjects.map(saved => (
                                            <div
                                                key={saved.project.id}
                                                className={`hero-card ${savedExpandedProjectId === saved.project.id ? 'selected' : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={saved.project.name ?? 'Untitled Project'}
                                                onClick={() => selectSavedProject(saved)}
                                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectSavedProject(saved); } }}
                                            >
                                                {(saved.project.graphicUrl || saved.project.logoUrl) ? (
                                                    <img
                                                        className="hero-card-image"
                                                        src={resolveUrl(saved.project.graphicUrl || saved.project.logoUrl)}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="hero-card-placeholder">
                                                        {(saved.project.name ?? 'P')[0]}
                                                    </div>
                                                )}
                                                <div className="hero-card-gradient" />
                                                <div className="hero-card-text">
                                                    <div className="hero-card-name">
                                                        {saved.project.name ?? 'Untitled Project'}
                                                    </div>
                                                    {saved.project.description && (
                                                        <div className="hero-card-desc">{saved.project.description}</div>
                                                    )}
                                                </div>
                                                <button
                                                    className="saved-remove-btn"
                                                    aria-label="Remove"
                                                    onClick={e => { e.stopPropagation(); onRemoveProject(saved.project.id!); if (savedExpandedProjectId === saved.project.id) { setSavedExpandedProjectId(null); setSavedExpandedScenes([]); } }}
                                                >
                                                    &times;
                                                </button>
                                                {savedLoadingProject && savedExpandedProjectId === saved.project.id && (
                                                    <div className="hero-card-loading">
                                                        <div className="loading-spinner" />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Expanded saved project's scenes */}
                                    {savedExpandedProjectId && !savedLoadingProject && (
                                        <div className="expanded-project-scenes">
                                            <h3 className="expanded-scenes-label">
                                                {savedProjects.find(s => s.project.id === savedExpandedProjectId)?.project.name ?? 'Project'} Scenes
                                            </h3>
                                            {savedExpandedScenes.length === 0 ? (
                                                <div className="expanded-scenes-empty">No scenes in this project</div>
                                            ) : (
                                                <div className="scenes-scroll" ref={savedScenesScrollRef}>
                                                    {savedExpandedScenes.map(scene => (
                                                        <div
                                                            key={scene.id}
                                                            className="scene-card-square"
                                                            onClick={() => onSelectScene(scene)}
                                                        >
                                                            {scene.imageUrl ? (
                                                                <img
                                                                    className="scene-card-image"
                                                                    src={resolveUrl(scene.imageUrl)}
                                                                    alt=""
                                                                />
                                                            ) : (
                                                                <div className="scene-card-placeholder">
                                                                    {(scene.name ?? 'S')[0]}
                                                                </div>
                                                            )}
                                                            <div className="scene-card-name">
                                                                {scene.name ?? 'Untitled Scene'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Saved independent scenes */}
                            {savedScenes.length > 0 && (
                                <div className="expanded-project-scenes">
                                    <h3 className="expanded-scenes-label">Saved Scenes</h3>
                                    <div className="scenes-scroll" ref={savedProjects.length === 0 ? savedScenesScrollRef : undefined}>
                                        {savedScenes.map(({ scene }) => (
                                            <div
                                                key={scene.id}
                                                className="scene-card-square saved-scene-card"
                                            >
                                                <div onClick={() => onSelectScene(scene)}>
                                                    {scene.imageUrl ? (
                                                        <img
                                                            className="scene-card-image"
                                                            src={resolveUrl(scene.imageUrl)}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <div className="scene-card-placeholder">
                                                            {(scene.name ?? 'S')[0]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="scene-card-name">
                                                    {scene.name ?? 'Untitled Scene'}
                                                </div>
                                                <button
                                                    className="saved-scene-remove-btn"
                                                    aria-label="Remove"
                                                    onClick={() => onRemoveScene(scene.id!)}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>{/* end saved tab */}

            {/* Bottom navigation */}
            <nav className="bottom-nav" aria-label="Main navigation">
                <button
                    className={`bottom-nav-item ${tab === 'experiences' ? 'active' : ''}`}
                    onClick={() => setTab('experiences')}
                    aria-current={tab === 'experiences' ? 'page' : undefined}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
                    </svg>
                    <span>Experience</span>
                </button>
                <button
                    className={`bottom-nav-item ${tab === 'saved' ? 'active' : ''}`}
                    onClick={() => setTab('saved')}
                    aria-current={tab === 'saved' ? 'page' : undefined}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                    </svg>
                    <span>Saved</span>
                </button>
                <button
                    className={`bottom-nav-item ${tab === 'map' ? 'active' : ''}`}
                    onClick={() => setTab('map')}
                    aria-current={tab === 'map' ? 'page' : undefined}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>Map</span>
                </button>
            </nav>

            {/* Code entry modal */}
            {showCodeModal && (
                <CodeModal
                    onSubmit={handleCodeSubmit}
                    onClose={() => { setShowCodeModal(false); setCodeError(null); }}
                    loading={codeLooking}
                    disabled={authLoading || !token}
                    error={codeError || initialCodeError || null}
                />
            )}
        </div>
    );
}
